import { lazy, Suspense, useCallback, useEffect } from 'react';
import { pageTitle } from '@/lib/page-title';
import { Link, Navigate, useLocation, useNavigate, useParams } from 'react-router-dom';
import { KEEP_SCROLL } from '@/lib/scroll-memory';
import { Trans, useTranslation } from 'react-i18next';
import { Check, Loader2 } from 'lucide-react';
import i18n from '@/lib/i18n';
import { trackEvent } from '@/lib/analytics';
import { useAuth } from '@/contexts/useAuth';
import { Navbar } from '@/components/layout/Navbar';
import { Footer } from '@/components/layout/Footer';
import { SystemChecks } from '@/components/trust/SystemChecks';
import { AnimateStatus, useAnimate } from '@/components/animate/useAnimate';
import { ShareToolBar } from '@/components/share/ShareToolBar';
import {
  editorLang,
  editorPageFor,
  editorPaid,
  editorPath,
  editorTool,
  type EditorLang,
  type EditorPageKey,
} from '@/components/editorPages/routes';

// The editor (and its WebGL engine) loads only on the editor pages, so the
// landing bundle every visitor downloads doesn't carry it.
const ChopBox = lazy(() => import('@/components/chopbox/ChopBox'));

const CHIPS = ['free', 'noSignup', 'noWatermark', 'onDevice'] as const;
// A paid page (Animate a photo) never says free or no sign-up about itself.
const PAID_CHIPS = ['edits', 'refund', 'gallery'] as const;

const STEPS = ['step1', 'step2', 'step3'] as const;
const FAQ = ['1', '2', '3', '4'] as const;

/**
 * THE EDITOR (2026-09-29, owner: "anyone should be able to edit and download
 * without signing up"; changed 2026-10-27: "a user shouldnt be able to use our
 * tools without registering on our site first", "Yes, editor too": the page is
 * public, the editor opens once signed in). /editor and one page per tool, each in
 * English and Spanish (components/editorPages/pages.json). ChopBox runs whole
 * on the visitor's device, so a visit costs us nothing; an account is only
 * asked for when they want Save to my gallery.
 *
 * Each slug has one language: opening a Spanish slug switches the site to
 * Spanish, and switching the language here moves to the other slug, so a URL
 * always shows the language its server-side <head> (lib/page-meta.js) says.
 */
export default function EditorPage() {
  const { slug } = useParams<{ slug?: string }>();
  const page = editorPageFor(slug);
  if (!page) return <Navigate to="/editor" replace />;
  return <EditorPageBody pageKey={page.key} lang={page.lang} />;
}

function EditorPageBody({ pageKey, lang }: { pageKey: EditorPageKey; lang: EditorLang }) {
  const { t } = useTranslation();
  const { user, isLoading, refreshUser } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const signedIn = !!user;
  // The paid "Animate this photo": offered to ChopBox only once the server says it runs.
  const animate = useAnimate({ signedIn, onCredits: () => void refreshUser?.() });
  const k = (key: string) => `editorPages.pages.${pageKey}.${key}`;
  const note = t(k('note'), { defaultValue: '' });

  // The slug's language wins on arrival; a later switch (the flag toggle) moves to the other slug.
  useEffect(() => {
    let armed = false;
    const onChange = (lng: string) => {
      if (!armed) return;
      const next = editorLang(lng);
      if (next !== lang) navigate(editorPath(pageKey, next) + location.hash, { replace: true, state: KEEP_SCROLL });
    };
    i18n.on('languageChanged', onChange);
    if (editorLang(i18n.language) !== lang) {
      void i18n.changeLanguage(lang).then(() => {
        armed = true;
      });
    } else {
      armed = true;
    }
    return () => {
      i18n.off('languageChanged', onChange);
    };
  }, [pageKey, lang, navigate, location.hash]);

  const title = `${t(k('metaTitle'))} | FaceShot-ChopShop`;
  useEffect(() => {
    const previous = document.title;
    document.title = pageTitle(title);
    return () => {
      document.title = previous;
    };
  }, [title]);

  useEffect(() => {
    trackEvent('editor_page_viewed', { page: pageKey, lang, signedIn });
    // Once per page and language; signing in on the page is not a new view.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pageKey, lang]);

  const here = editorPath(pageKey, lang);
  const onSignIn = useCallback(() => {
    trackEvent('editor_signup_clicked', { page: pageKey });
    navigate(`/signup?redirect_to=${encodeURIComponent(here)}`);
  }, [navigate, here, pageKey]);
  const onShowSaved = useCallback(() => navigate('/chopshop#my-gallery'), [navigate]);

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="pt-24">
        <div className="container mx-auto px-4">
          <header className="mx-auto max-w-3xl pb-6 text-center">
            <p className="text-[12px] font-semibold uppercase tracking-[0.2em] text-primary" translate="no">
              {t('editorPages.common.eyebrow')}
            </p>
            <h1 className="mt-3 text-balance font-display text-[30px] font-bold leading-[1.08] tracking-[-0.02em] sm:text-[40px]">
              {t(k('h1'))}
            </h1>
            <p className="mx-auto mt-4 max-w-2xl text-pretty text-[15px] leading-relaxed text-muted-foreground sm:text-[1.05rem]">
              {t(k('lead'))}
            </p>
            <ul className="mt-5 flex flex-wrap justify-center gap-2" aria-label={t(k('h1'))}>
              {(editorPaid(pageKey) ? PAID_CHIPS.map((c) => `paidChips.${c}`) : CHIPS.map((c) => `chips.${c}`)).map((chip) => (
                <li
                  key={chip}
                  className="inline-flex items-center gap-1.5 rounded-full border border-border bg-card px-3 py-1 text-[13px] text-foreground"
                >
                  <Check className="h-3.5 w-3.5 text-primary" aria-hidden />
                  {t(`editorPages.common.${chip}`)}
                </li>
              ))}
            </ul>
            <ShareToolBar path={typeof window !== 'undefined' ? window.location.pathname : '/editor'} align="center" className="mt-5" />
            {note ? <p className="mt-4 text-[13px] text-muted-foreground">{note}</p> : null}
          </header>

          {/* ChopBox. No ancestor of it may get a transform, filter,
              backdrop-filter, contain: paint or will-change: transform: its
              full screen mode is position: fixed. */}
          <section id="chopbox" aria-label={t(k('h1'))} className="mb-4 scroll-mt-20 rounded-lg bg-card p-3 sm:p-4">
            {/* Registered first (owner 2026-10-27: "a user shouldnt be able to use our tools without registering on our
                site first"; the editor too: "Yes, editor too"): signed out, a sign-up / log-in card that comes back here. */}
            {isLoading ? (
              <EditorLoading />
            ) : !signedIn ? (
              <div data-testid="editor-sign-in-first" className="flex min-h-[320px] flex-col items-center justify-center px-4 py-10 text-center">
                <h2 className="text-balance font-display text-[22px] font-bold sm:text-[26px]">{t('qt.signInFirst.editorTitle')}</h2>
                <p className="mx-auto mt-3 max-w-md text-pretty text-[15px] text-muted-foreground">{t('qt.signInFirst.editorHint')}</p>
                <div className="mt-6 flex w-full max-w-sm flex-col gap-3 sm:flex-row sm:justify-center">
                  <Link to={`/signup?redirect_to=${encodeURIComponent(here)}`} onClick={() => trackEvent('editor_signup_clicked', { page: pageKey })}
                    className="inline-flex min-h-[48px] items-center justify-center rounded-xl bg-primary px-6 text-[15px] font-bold text-primary-foreground">{t('qt.signInFirst.signup')}</Link>
                  <Link to={`/login?redirect_to=${encodeURIComponent(here)}`} onClick={() => trackEvent('editor_login_clicked', { page: pageKey })}
                    className="inline-flex min-h-[48px] items-center justify-center rounded-xl border border-border px-6 text-[15px] font-semibold text-foreground">{t('qt.signInFirst.login')}</Link>
                </div>
              </div>
            ) : (
              <Suspense fallback={<EditorLoading />}>
                <ChopBox
                  signedIn={signedIn}
                  onSignIn={onSignIn}
                  onShowSaved={onShowSaved}
                  preferredTool={editorTool(pageKey)}
                  animate={animate.offer}
                />
              </Suspense>
            )}
          </section>
          <AnimateStatus job={animate.job} credits={animate.credits} onDismiss={animate.dismiss} />
          <p className="mb-12 text-center text-[13px] text-muted-foreground">
            {signedIn ? (
              <Trans i18nKey="editorPages.common.savedHere" components={{ a: <Link to="/chopshop#my-gallery" className="text-primary underline" /> }} />
            ) : (
              t('editorPages.common.signUpNote')
            )}
          </p>

          <section className="mx-auto mb-14 max-w-4xl" aria-labelledby="editor-how">
            <h2 id="editor-how" className="mb-5 text-center font-display text-[22px] font-bold sm:text-[26px]">
              {t('editorPages.common.howTitle')}
            </h2>
            <ol className="grid gap-3 sm:grid-cols-3">
              {STEPS.map((step, i) => (
                <li key={step} className="rounded-2xl border border-border bg-card p-5">
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/15 text-[14px] font-bold text-primary" aria-hidden>
                    {i + 1}
                  </span>
                  <p className="mt-3 text-[15px] leading-relaxed text-foreground">{t(k(step))}</p>
                </li>
              ))}
            </ol>
          </section>

          <SystemChecks checks={['editorOnDevice']} className="mb-16" />

          <section className="mx-auto mb-16 max-w-3xl" aria-labelledby="editor-faq">
            <h2 id="editor-faq" className="mb-5 text-center font-display text-[22px] font-bold sm:text-[26px]">
              {t('editorPages.common.faqTitle')}
            </h2>
            <div className="space-y-3">
              {FAQ.map((n) => (
                <details key={n} className="group rounded-2xl border border-border bg-card p-5 open:border-primary/40">
                  <summary className="cursor-pointer list-none text-[15px] font-semibold text-foreground">
                    {t(`editorPages.common.faq.q${n}`)}
                  </summary>
                  <p className="mt-3 text-[15px] leading-relaxed text-muted-foreground">{t(`editorPages.common.faq.a${n}`)}</p>
                </details>
              ))}
            </div>
          </section>
        </div>
      </main>
      <Footer />
    </div>
  );
}

function EditorLoading() {
  const { t } = useTranslation();
  return (
    <div role="status" className="flex min-h-[320px] items-center justify-center rounded-lg bg-background/40">
      <Loader2 className="h-6 w-6 animate-spin text-primary" aria-hidden />
      <span className="sr-only">{t('app.loading')}</span>
    </div>
  );
}
